import React, {useContext, useEffect} from "react";
import { useState } from "react";
import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/outline";
import menu from "./menu";
import UserBox from "./UserBox";
import Logo from "../../components/Logo";
import MenuLink from "./MenuLink";
import filterRoutesByScopes from "../../utils/filterRoutesByScopes";
import LanguageSelector from "./LanguageSelector";
import MenuStoreSelector from "./MenuStoreSelector";
import CountryContext from "../../CountryContext";
import UserNotificationsContext from "../../UserNotificationsContext";
import NotificationPing from "../NotificationPing";
import AuthenticationService from "../../services/AuthenticationService";
import { NotificationsContext } from "../Notifications";
import { useIntl } from "react-intl";
import NotificationsBox from "@ui-components/AppNavigation/NotificationsBox";
import {ChevronLeft} from "@ui-components/icons/ChevronLeft";
import {ChevronRight} from "@ui-components/icons/ChevronRight";
import {useHomeLinks} from "@feature/home/hooks/useHomeLinks";


const NOTIFICATION_MAPPINGS = {
  worksites: (n) => !!n.cod_worksite && n.val_type !== "worksite_reference_report",
  documents: {
    references: (n) =>
      n.val_type === "worksite_reference_report"
      || (
        !!n.cod_reference
        && n.reference.flg_active
        && ["title", "subtitle", "project", "description"].every(
          (content_field) => !!n.reference.content?.[content_field]
        )
      )
  }
}


function AppNavigation({ children }) {

  // context
  const { country } = useContext(CountryContext);
  const { userNotifications, setUserNotifications } = useContext(UserNotificationsContext);
  const { push } = useContext(NotificationsContext);

  // hooks
  const intl = useIntl();
  const { data: homeLinks } = useHomeLinks();

  // state
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState();

  // utilities
  const userData = AuthenticationService.getUserData();
  const menuNotifications = !!userNotifications
    ? Object.entries(NOTIFICATION_MAPPINGS).reduce(
      (acc, [key, logic]) => {
        const counter = typeof logic === "function"
          ? userNotifications.filter(logic).length
          : Object.entries(logic).reduce(
            (innerAcc, [innerKey, innerLogic]) => ({
              ...innerAcc,
              [innerKey]: userNotifications.filter(innerLogic).length,
            }),
            {}
          );
        if (typeof counter === "object")
          counter["total"] = Object.values(counter).reduce((acc, curr) => acc + curr, 0);
        return {...acc, [key]: counter};
      },
      {}
    )
    : null;
  const getNotificationsCount = (...names) => {
    if (!menuNotifications)
      return 0;
    const [outer, inner] = names.concat([null]).splice(0, 2);
    const counter = menuNotifications[outer];
    if (typeof counter === "object")
      return !!inner ? (counter[inner] || 0) : counter.total;
    return counter;
  }
  const generateMenuItems = (slim) => filterRoutesByScopes(menu, country.code)
    .map((i) => ({
      ...i,
      notificationsCount: getNotificationsCount(i.name),
      dropdown: i.dropdown
        ?.map((d) => ({
          ...d,
          notificationsCount: getNotificationsCount(i.name, d.name),
        })),
    }))
    .map((i) => (
      <MenuLink
        key={i.name}
        slim={slim}
        isDropdownOpen={dropdownOpen === i.name}
        toggleDropdown={() => {
          setDropdownOpen((prev) =>
            !!i.dropdown && prev !== i.name
              ? i.name
              : undefined
          );
        }}
        closeDropdown={() => { setDropdownOpen(undefined); }}
        homeLinks={homeLinks}
        {...i}
      />
    ));

  useEffect(() => {
    // close dropdowns when sidebar is closed
    if (sidebarOpen) return;
    setDropdownOpen(undefined);
  }, [sidebarOpen, setDropdownOpen]);

  const wipeNotifications = () => {
    setUserNotifications((prev) => prev.filter((n) => n.cod_user !== userData.id));
    push({type: "success", title: intl.formatMessage({ id: "notifications_successfully_deleted" })});
  };

  return <>
    <div className="min-h-full md:h-full flex">

      {/* ------------------------------------------------------------ */}
      {/*                     Mobile-only sidebar                      */}
      {/* ------------------------------------------------------------ */}

      <div className={`fixed inset-0 z-40 ${sidebarOpen ? "lg:hidden" : "hidden"} bg-black bg-opacity-40`}>
        <div className="relative h-full flex flex-col px-6 w-5/6 sm:w-2/3 md:w-1/2 bg-white focus:outline-none">
          <div className="absolute top-0 right-0 px-3 py-4">
            <button
              type="button"
              className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-am-500 my-0.5"
              onClick={() => setSidebarOpen(false)}
            >
              <span className="sr-only">Close sidebar</span>
              <XMarkIcon
                className="h-6 w-6 text-am-500"
                aria-hidden="true"
              />
            </button>
          </div>
          <div className="flex-1 flex flex-col h-0 py-6">
            <Logo business={userData.business} width="w-60" />
            <nav
              aria-label="Sidebar"
              className="mt-6 flex-col flex justify-between flex-1 h-full"
            >
              <div>
                <UserBox />
                <NotificationsBox onClick={() => { wipeNotifications(); }} />
                <hr className="w-full my-3 border-gray-300" />
                {generateMenuItems(false)}
              </div>
              <div className="w-full">
                <LanguageSelector />
                <MenuStoreSelector />
              </div>
            </nav>
          </div>
        </div>
      </div>

      {/* ------------------------------------------------------------ */}
      {/*                     Desktop-only sidebar                     */}
      {/* ------------------------------------------------------------ */}

      <div className="hidden lg:flex lg:flex-shrink-0">
        <div className={`relative flex flex-col bg-white ${sidebarOpen ? "w-80" : "w-28"}`}>
          <div
            className={`absolute w-7 h-7 rounded-full border -right-3 ${sidebarOpen ? "top-5" : "top-14"} shadow bg-ivory text-gray-700 hover:bg-white cursor-pointer flex items-center justify-center`}
            onClick={() => { setSidebarOpen((prev) => !prev); }}
          >
            {sidebarOpen ? <ChevronLeft className="h-4" /> : <ChevronRight className="h-4" />}
          </div>
          <div className={`flex-1 flex flex-col ${sidebarOpen ? "py-6 px-5" : "py-4 px-3"} min-h-0 border-r border-gray-200`}>
            <div className="flex-1 flex flex-col">
              <Logo
                business={userData.business}
                containerCls={sidebarOpen ? "" : "hidden"}
                width="w-52"
              />
              <Logo
                business={userData.business}
                containerCls={sidebarOpen ? "hidden" : ""}
                slim
              />
              <nav
                className={`${sidebarOpen ? "mt-4" : "mt-8"} flex-1 flex flex-col items-center justify-between gap-6`}
                aria-label="Sidebar"
              >
                <div className="flex flex-col gap-1 items-center w-full">
                  <UserBox
                    slim={!sidebarOpen}
                    slimDropdownOpen={dropdownOpen === "__user"}
                    toggleSlimDropdown={() => {
                      setDropdownOpen((prev) =>
                        prev !== "__user" ? "__user" : undefined
                      );
                    }}
                    closeSlimDropdown={() => { setDropdownOpen(undefined); }}
                  />
                  <NotificationsBox
                    slim={!sidebarOpen}
                    onClick={() => {
                      if (!sidebarOpen) setSidebarOpen(true);
                      else wipeNotifications();
                    }}
                  />
                  <hr className="w-full my-3 border-gray-300" />
                  {generateMenuItems(!sidebarOpen)}
                </div>
                <div className="w-full">
                  <LanguageSelector slim={!sidebarOpen} />
                  <MenuStoreSelector slim={!sidebarOpen} />
                </div>
              </nav>
            </div>
          </div>
        </div>
      </div>

      {/* ------------------------------------------------------------ */}
      {/*                        Main container                        */}
      {/* ------------------------------------------------------------ */}

      <div className="flex flex-col min-w-0 flex-1 overflow-hidden">
        <div className="lg:hidden">
          <div className="flex items-center justify-between border-b border-gray-200 py-1.5 px-4">
            <div>
              <Logo business={userData.business} width="w-52" />
            </div>
            <div className="px-4">
              <button
                type="button"
                className="-mr-5 h-12 w-12 inline-flex items-center justify-center text-gray-500 hover:text-black-500 relative"
                onClick={() => setSidebarOpen(true)}
              >
                <span className="sr-only">Open sidebar</span>
                <Bars3Icon className="h-6 w-6" aria-hidden="true" />
                {userNotifications?.length > 0 && (
                  <span className="absolute top-2.5 right-2">
                    <NotificationPing />
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
        <div className="flex-1 relative max-w-full md:overflow-y-scroll">
          <div className="flex flex-col flex-1 max-w-full">
            <main
              id="main"
              className="flex-1 relative z-0 overflow-y-auto focus:outline-none xl:order-last max-w-full"
            >
              {children}
            </main>
          </div>
        </div>
      </div>
    </div>
  </>
}

export default AppNavigation;
